import * as services from "../services/services";
import { toast } from "react-hot-toast";

/**
 * Formats a date to YYYY-MM-DD as required by the Bow API.
 * @param {Date|string} date 
 */
const formatDateBow = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/**
 * Converts a string (Base64 with prefix or URL) to raw Base64 as required by Bow API.
 * @param {string} input 
 */
const toBase64Bow = async (input) => {
    if (!input || typeof input !== "string") return null;

    // If it's a URL
    if (input.startsWith("http")) {
        try {
            const response = await fetch(input);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.split(",")[1];
                    resolve(base64String);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (e) {
            console.error("Failed to fetch image from URL", e);
            return null;
        }
    }

    // If it has Data URL prefix
    if (input.startsWith("data:image/")) {
        return input.split(",")[1];
    }

    // Heuristic: if it looks like base64 (long enough and contains typical chars)
    if (input.length > 50) {
        return input;
    }

    return null;
};

/**
 * Synchronizes historical hospital records (vaccines and parasites) to the Bow system.
 * @param {number|string} petId - The local hospital pet ID.
 * @param {string} bowPetId - The external Bow pet ID.
 */
export const syncPetHistoryToBow = async (petId, bowPetId) => {
    if (!petId || !bowPetId) return;

    const toastId = toast.loading("กำลังซิงก์ประวัติย้อนหลังไปยังระบบ Bow...");

    try {
        const res = await services.getPetById(petId);
        const petData = res.data.data;

        if (!petData || !petData.treatments) {
            toast.dismiss(toastId);
            return;
        }

        const treatments = petData.treatments;
        let syncCount = 0;

        for (const treatment of treatments) {
            const { serviceType, serviceDate, note, handledBy, hospital, hospitalId, PetVaccineRecord, petParasiteRecords } = treatment;

            if (serviceType === "vaccine" && PetVaccineRecord?.length > 0) {
                const items = await Promise.all(PetVaccineRecord.map(async (v) => {
                    const rawPhoto = await toBase64Bow(v.photo);
                    return {
                        product: v.product,
                        lot_number: v.lotNumber || "",
                        expiration_date: formatDateBow(v.expirationDate),
                        appointed_date: formatDateBow(v.appointedDate),
                        photos: rawPhoto ? [{ photo: rawPhoto, is_label: true }] : []
                    };
                }));

                const payload = {
                    pet: bowPetId,
                    date: formatDateBow(serviceDate),
                    note: note || "",
                    items: items,
                    vet_name: `${handledBy?.name || ""} ${handledBy?.lastname || ""}`.trim(),
                    vet_license_number: handledBy?.license_no || "",
                    vet_hospital_name: hospital?.name || "",
                    created_hospital_id: hospitalId
                };
                await services.putVaccineRecordsBow(payload);
                syncCount++;
            } else if (serviceType === "parasite" && petParasiteRecords?.length > 0) {
                const items = await Promise.all(petParasiteRecords.map(async (p) => {
                    const rawPhoto = await toBase64Bow(p.photo);
                    return {
                        product: p.product,
                        lot_number: p.lotNumber || "",
                        expiration_date: formatDateBow(p.expirationDate),
                        appointed_date: formatDateBow(p.appointedDate),
                        photos: rawPhoto ? [{ photo: rawPhoto, is_label: true }] : []
                    };
                }));

                const payload = {
                    pet: bowPetId,
                    date: formatDateBow(serviceDate),
                    note: note || "",
                    items: items,
                    vet_name: `${handledBy?.name || ""} ${handledBy?.lastname || ""}`.trim(),
                    vet_license_number: handledBy?.license_no || "",
                    vet_hospital_name: hospital?.name || "",
                    created_hospital_id: hospitalId
                };
                await services.createParasiteRecordsBow(payload);
                syncCount++;
            }
        }

        if (syncCount > 0) {
            toast.success(`ซิงก์ข้อมูลสำเร็จ ${syncCount} รายการ`, { id: toastId });
        } else {
            toast.dismiss(toastId);
        }
    } catch (error) {
        console.error("Failed to sync historical records", error);

        let errorMsg = "เกิดข้อผิดพลาดในการซิงก์ข้อมูลย้อนหลัง";

        if (error.response?.data) {
            const data = error.response.data;
            if (data.date?.[0]) {
                errorMsg = `รูปแบบวันที่ไม่ถูกต้อง: ${data.date[0]}`;
            } else if (data.items?.[0]) {
                const itemError = data.items[0];
                const firstKey = Object.keys(itemError)[0];
                if (firstKey) {
                    const subError = typeof itemError[firstKey] === 'object' ? JSON.stringify(itemError[firstKey]) : itemError[firstKey];
                    errorMsg = `ข้อมูลรายการไม่ถูกต้อง (${firstKey}): ${subError}`;
                }
            } else if (data.message) {
                errorMsg = data.message;
            }
        }

        toast.error(errorMsg, { id: toastId, duration: 5000 });
    }
};
